import { DialogComponent } from '@theme/dialog';

/**
 * A flat single-level size selector drawer, used by the default size picker for ranges with many
 * sizes (10+). A collapsed trigger opens a right-side drawer (bottom sheet on mobile) listing every
 * size; the inline broken-out grid is used for shorter ranges instead.
 *
 * This element owns only the drawer UI (open/close + the collapsed trigger label). Variant selection
 * is delegated to a sibling `<variant-picker>` (located via `data-picker-selector`) that holds the
 * real, visually-hidden radios. Picking a size proxy-clicks the matching radio there, so the theme's
 * existing variant machinery (price, `?variant=`, add-to-cart, section morph) runs unchanged. Keeping
 * the drawer outside the picker means the picker's self-morph on every size change never clobbers the
 * open `<dialog>`. Selecting closes the drawer — there is no in-drawer add-to-cart button (per design).
 *
 * @extends {DialogComponent}
 */
class SizeDrawer extends DialogComponent {
  /** @returns {Element | null} The sibling variant picker that holds the real radios. */
  get picker() {
    return document.querySelector(this.dataset.pickerSelector || '');
  }

  /** @returns {HTMLElement | null} The collapsed trigger button. */
  get trigger() {
    return /** @type {HTMLElement | null} */ (document.querySelector(this.dataset.triggerSelector || ''));
  }

  showDialog() {
    super.showDialog();
    // Reflect the current selection (if the shopper has chosen one) as the underlined row.
    this.#syncToSelection();
  }

  /**
   * Size row clicked → select the matching variant via the sibling picker, mark it, and close.
   * @param {Event} event
   */
  select(event) {
    const button = /** @type {HTMLElement | null} */ (
      /** @type {HTMLElement} */ (event.target).closest('[data-option-value-id]')
    );
    if (!button || /** @type {HTMLButtonElement} */ (button).disabled) return;

    // Read the id from the DOM as a string. Never pass it through an `on:click` "?payload": the
    // component event delegator Number()-coerces those, losing precision on option-value ids
    // larger than Number.MAX_SAFE_INTEGER.
    const optionValueId = button.getAttribute('data-option-value-id');
    const radio = /** @type {HTMLInputElement | null} */ (
      this.picker?.querySelector(`input[data-option-value-id="${optionValueId}"]`) ?? null
    );
    if (!radio || radio.dataset.optionAvailable === 'false') return;

    radio.checked = true;
    // Drive the existing variant-picker change handler (it reads event.target's dataset).
    radio.dispatchEvent(new Event('change', { bubbles: true }));
    this.#markSelected(button);
    this.#setTriggerLabel(button.getAttribute('data-label'));
    this.closeDialog();
  }

  /**
   * On open, underline the currently selected size's row — but only once a size has actually been
   * chosen (tracked on the trigger), so a fresh product page opens with nothing underlined rather
   * than highlighting the auto first-available variant.
   */
  #syncToSelection() {
    if (this.trigger?.dataset.selected !== 'true') return;
    const checked = /** @type {HTMLInputElement | null} */ (
      this.picker?.querySelector('input[type="radio"]:checked') ?? null
    );
    const id = checked?.dataset.optionValueId;
    if (!id) return;
    const button = /** @type {HTMLElement | null} */ (
      this.refs.dialog.querySelector(`.size-drawer__row[data-option-value-id="${id}"]`)
    );
    if (button) this.#markSelected(button);
  }

  /**
   * Mark a single row as the selection (the underline state); clears any previous one.
   * @param {HTMLElement} button
   */
  #markSelected(button) {
    for (const el of this.refs.dialog.querySelectorAll('.size-drawer__row--selected')) {
      el.classList.remove('size-drawer__row--selected');
      el.removeAttribute('aria-pressed');
    }
    button.classList.add('size-drawer__row--selected');
    button.setAttribute('aria-pressed', 'true');
  }

  /**
   * Mark the trigger as selected and set its label (e.g. "32").
   * @param {string | null} label
   */
  #setTriggerLabel(label) {
    const { trigger } = this;
    if (!trigger) return;
    trigger.dataset.selected = 'true';
    const labelEl = trigger.querySelector('.size-picker__trigger-label');
    if (labelEl && label) labelEl.textContent = label;
  }
}

if (!customElements.get('size-drawer')) {
  customElements.define('size-drawer', SizeDrawer);
}
